import React, { useCallback, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import Icon from 'components/media/Icon/index.js';
import { useHash } from 'utils/urlUtils.js';

const Badge = styled.div`
  display: inline-block;
  width: 1.5em;
  height: 1.5em;
  border-radius: 50%;
  text-align: center;
  color: white;
  margin-right: 1rem;
  background-color: ${(p) => p.theme.brandPrimary}
`;

const Wrapper = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1.5rem;
  i.ss-chevron-right {
    color: ${(p) => p.theme.grayLight};
  }
  a:not(.active), button:not(.active) {
    filter: saturate(0);
  }
  a[disabled], button[disabled] {
    opacity: 0.5;
  }
`;

function useStep(hashProp) {
  const [state, setState] = useState();
  const hash = useHash();
  const setHash = useCallback((v, repl) => window.setTimeout(() => {
    const func = repl ? hash.replace : hash.push;
    func(hashProp, v);
  }, 0), [hashProp, hash]);
  return hashProp ? [hash[hashProp], setHash] : [state, setState];
}

/**
 *
 * @param {array} steps [ { obj:label, func:disabled}]
 * @param {string} hashProp property name in hash string, if empty use state
 */
function useWizard(steps, hashProp) {
  const [currentStep, setActive] = useStep(hashProp);

  function adjustStep() {
    if (!currentStep || !steps[currentStep - 1]) { // one higher than index in step
      return 1;
    }
    for (let i = currentStep; i >= 1; i--) {
      const step = steps[i - 1];
      const disabled = step.disabled?.();
      if (!disabled) {
        return i;
      }
    }
    return 1;
  }
  const activeStep = useMemo(adjustStep, [currentStep, steps]);

  useEffect(() => {
    if (activeStep !== currentStep) {
      setActive(activeStep, true);
    } else {
      document.querySelector('body')?.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [activeStep, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps
  const stepsWithIndex = steps.map((step, idx) => ({ disabled: () => false, ...step, id: idx + 1 }));
  const StepNav = useCallback(() => (
    <Wrapper>
      {stepsWithIndex.map((step, idx) => (
        <React.Fragment key={step.id}>
          { idx > 0 && (<Icon className="ss-chevron-right" />)}
          <Button variant="link" disabled={step.disabled()} active={step.id === activeStep} onClick={() => setActive(step.id)}>
            <Badge>{step.id}</Badge>
            <FormattedMessage {...step.label} />
          </Button>
        </React.Fragment>
      ))}
    </Wrapper>
  ), [stepsWithIndex, activeStep, setActive]);
  return { activeStep, StepNav, setActive };
}

export default useWizard;
